import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { map, Observable } from 'rxjs';
import {
  Deeplink,
  EpisodeDeeplink,
  Notification,
  NotificationApiResponse,
  PodcastDeeplink,
  RadioDeeplink,
  transformNotification,
} from '../models/notification.model';
import { TargetedSubscribers } from '../states/notifications.state';

const NOTIFICATION_URLS = {
  getNotifications: (id: number, page: number) =>
    `${environment.urls.MOBILE_API}/app/${id}/notification?limit=20&sort=-send_at&page=${page}`,
  sendNotification: (id: number) =>
    `${environment.urls.MOBILE_API}/app/${id}/notification`,
  updateNotification: (appId: number, notificationId: number) =>
    `${environment.urls.MOBILE_API}/app/${appId}/notification/${notificationId}`,
  deleteNotification: (appId: number, notificationId: number) =>
    `${environment.urls.MOBILE_API}/app/${appId}/notification/${notificationId}`,
  getTargetedSubscribers: (
    appId: number,
    location: string,
    lang: string,
    platform: string,
  ) =>
    `${environment.urls.MOBILE_API}/app/${appId}/subscriber/count?location=${location}&lang=${lang}&platform=${platform}&group=platform`,
  getDeepLinks: (appId: number) =>
    `${environment.urls.MOBILE_API}/app/${appId}/deeplinks`,
  getPodcastDeeplinks: (appId: number, selectedDeeplink: string) =>
    `${environment.urls.MOBILE_API}/app/${appId}/podcast-deeplinks?deeplink=${selectedDeeplink}`,
  getEpisodeDeeplinks: (appId: number, selectedDeeplink: string, base64RssFeed: string) =>
    `${environment.urls.MOBILE_API}/app/${appId}/episode-deeplinks?deeplink=${selectedDeeplink}&url=${base64RssFeed}`,
  getRadioDeeplinks: (appId: number) =>
    `${environment.urls.MOBILE_API}/app/${appId}/radio-deeplinks`,
};

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  constructor(private readonly http: HttpClient) {}

  getNotifications(id: number, page: number): Observable<NotificationApiResponse> {
    return this.http.get<NotificationApiResponse>(
      NOTIFICATION_URLS.getNotifications(id, page),
    );
  }

  sendNotification(notification: Partial<Notification>, id: number): Observable<any> {
    return this.http.post(
      NOTIFICATION_URLS.sendNotification(id),
      transformNotification(notification),
    );
  }

  updateNotification(
    appId: number,
    notificationId: number,
    notification: Partial<Notification>,
  ): Observable<any> {
    return this.http.put(
      NOTIFICATION_URLS.updateNotification(appId, notificationId),
      transformNotification(notification),
    );
  }

  deleteNotification(appId: number, notificationId: number): Observable<any> {
    return this.http.delete(NOTIFICATION_URLS.deleteNotification(appId, notificationId));
  }

  getTargetedSubscribers(
    appId: number,
    location: string,
    lang: string,
    platform: string,
  ): Observable<TargetedSubscribers> {
    return this.http
      .get<{ platform: string; count: number }[]>(
        NOTIFICATION_URLS.getTargetedSubscribers(appId, location, lang, platform),
      )
      .pipe(map(data => convertToTargetedSubscribers(data)));
  }

  getDeepLinks(appId: number): Observable<Deeplink[]> {
    return this.http.get<Deeplink[]>(NOTIFICATION_URLS.getDeepLinks(appId));
  }

  getPodcastDeeplinks(appId: number, selectedDeeplink: string): Observable<PodcastDeeplink[]> {
    return this.http.get<PodcastDeeplink[]>(NOTIFICATION_URLS.getPodcastDeeplinks(appId, selectedDeeplink));
  }

  getEpisodeDeeplinks(appId: number, selectedDeeplink: string, base64RssFeed: string): Observable<EpisodeDeeplink[]> {
    return this.http.get<EpisodeDeeplink[]>(NOTIFICATION_URLS.getEpisodeDeeplinks(appId, selectedDeeplink, base64RssFeed));
  }

  getRadioDeeplinks(appId: number): Observable<RadioDeeplink[]> {
    return this.http.get<RadioDeeplink[]>(NOTIFICATION_URLS.getRadioDeeplinks(appId));
  }
}

function convertToTargetedSubscribers(
  data: { platform: string; count: number }[],
): TargetedSubscribers {
  const result: TargetedSubscribers = { ios: 0, android: 0 };
  data.forEach(item => {
    if (item.platform === 'iphone') {
      result.ios = item.count;
    } else if (item.platform === 'android') {
      result.android = item.count;
    }
  });

  return result;
}
